import type { PayloadAction } from "@reduxjs/toolkit";
import type {
	ReapplyJobIdentity,
	ReapplyRecipesSelectionState
} from "~produce/production/schedule/helpers/reapplyRecipesSelectionTypes";

import { createSlice } from "@reduxjs/toolkit";

import { type GetReapplyRecipesPreviewQuery } from "~/gql/queries/produce/reapplyRecipes.generated";
import { findBlockedChildReason } from "~produce/production/schedule/helpers/reapplyBlockedReason";
import { buildDefaultJobSelection } from "~produce/production/schedule/helpers/reapplyRecipesDefaults";
import { mergeResumedReapplyRunIds } from "~produce/production/schedule/helpers/reapplyRecipesRun";
import { eventIdChangeKey } from "~produce/production/schedule/helpers/reapplyRecipesSelectionTypes";

type Preview = GetReapplyRecipesPreviewQuery["reapplyRecipesPreview"][number];

export interface ReapplyRecipesState extends ReapplyRecipesSelectionState {
	activeStep: number;
	finishedRunIds: string[];
	jobIdentities: Record<string, ReapplyJobIdentity>;
	jobIds: string[];
	open: boolean;
	previewByJobId: Record<string, Preview>;
	viewedRunId: string | null;
	watchedRunIds: string[];
}

const initialState: ReapplyRecipesState = {
	activeStep: 0,
	cascadeChildJobs: {},
	finishedRunIds: [],
	jobIdentities: {},
	jobIds: [],
	jobs: {},
	open: false,
	previewByJobId: {},
	viewedRunId: null,
	watchedRunIds: []
};

const reapplyRecipesSlice = createSlice({
	initialState,
	name: "reapplyRecipes",
	reducers: {
		closeReapplyRecipes: (state) => {
			state.activeStep = 0;
			state.cascadeChildJobs = {};
			state.jobIdentities = {};
			state.jobIds = [];
			state.jobs = {};
			state.open = false;
			state.previewByJobId = {};
		},
		closeReapplyRunView: (state) => {
			state.viewedRunId = null;
		},
		finishReapplyRun: (state, action: PayloadAction<string>) => {
			state.watchedRunIds = state.watchedRunIds.filter((runId) => runId !== action.payload);
			if (!state.finishedRunIds.includes(action.payload)) state.finishedRunIds.push(action.payload);
		},
		openReapplyRecipes: (
			state,
			action: PayloadAction<{ jobIdentities?: Record<string, ReapplyJobIdentity>; jobIds: string[] }>
		) => {
			state.activeStep = 0;
			state.cascadeChildJobs = {};
			state.jobIdentities = action.payload.jobIdentities ?? {};
			state.jobIds = action.payload.jobIds;
			state.jobs = {};
			state.open = true;
			state.previewByJobId = {};
		},
		openReapplyRunView: (state, action: PayloadAction<string>) => {
			state.viewedRunId = action.payload;
		},
		resumeReapplyRuns: (state, action: PayloadAction<string[]>) => {
			state.watchedRunIds = mergeResumedReapplyRunIds(state.watchedRunIds, action.payload, state.finishedRunIds);
		},
		setActiveStep: (state, action: PayloadAction<number>) => {
			state.activeStep = action.payload;
		},
		setCascadeChildJob: (state, action: PayloadAction<{ childJobId: string; included: boolean }>) => {
			const { childJobId, included } = action.payload;
			if (included && findBlockedChildReason(Object.values(state.previewByJobId), childJobId)) return;
			state.cascadeChildJobs[childJobId] = included;
		},
		setJobEnabled: (state, action: PayloadAction<{ jobId: string; enabled: boolean }>) => {
			const { jobId, enabled } = action.payload;
			const job = state.jobs[jobId];
			if (!job) return;
			if (enabled && state.previewByJobId[jobId]?.blockedReason) return;
			job.enabled = enabled;
		},
		trackStartedReapplyRun: (state, action: PayloadAction<string>) => {
			if (!state.watchedRunIds.includes(action.payload)) state.watchedRunIds.push(action.payload);
			state.viewedRunId = action.payload;
		},
		untrackReapplyRun: (state, action: PayloadAction<string>) => {
			state.watchedRunIds = state.watchedRunIds.filter((runId) => runId !== action.payload);
		},
		updateAssignmentOverrideSelection: (
			state,
			action: PayloadAction<{ jobId: string; variationId: string; eventId: string; selected: boolean }>
		) => {
			const { jobId, variationId, eventId, selected } = action.payload;
			const variation = state.jobs[jobId]?.variations[variationId];
			if (!variation) return;
			const set = new Set(variation.assignmentOverrideEventIds);
			if (selected) set.add(eventId);
			else set.delete(eventId);
			variation.assignmentOverrideEventIds = [...set];
		},
		updateEventIdChangeSelection: (
			state,
			action: PayloadAction<{
				jobId: string;
				variationId: string;
				eventListName: string;
				eventId: string;
				selected: boolean;
			}>
		) => {
			const { jobId, variationId, eventListName, eventId, selected } = action.payload;
			const variation = state.jobs[jobId]?.variations[variationId];
			if (!variation) return;
			const key = eventIdChangeKey(eventListName, eventId);
			const set = new Set(variation.eventIdChangeKeys);
			if (selected) set.add(key);
			else set.delete(key);
			variation.eventIdChangeKeys = [...set];
		},
		updateRemovedVariationSelection: (
			state,
			action: PayloadAction<{ jobId: string; variationId: string; selected: boolean }>
		) => {
			const { jobId, variationId, selected } = action.payload;
			const job = state.jobs[jobId];
			if (!job) return;
			const set = new Set(job.removedVariationIds);
			if (selected) set.add(variationId);
			else set.delete(variationId);
			job.removedVariationIds = [...set];
		},
		updateScalarFieldSelection: (
			state,
			action: PayloadAction<{ jobId: string; variationId: string; fieldName: string; selected: boolean }>
		) => {
			const { jobId, variationId, fieldName, selected } = action.payload;
			const variation = state.jobs[jobId]?.variations[variationId];
			if (!variation) return;
			const set = new Set(variation.scalarFieldNames);
			if (selected) set.add(fieldName);
			else set.delete(fieldName);
			variation.scalarFieldNames = [...set];
		},
		upsertPreviewResults: (state, action: PayloadAction<Preview[]>) => {
			for (const preview of action.payload) {
				const wasBlocked = Boolean(state.previewByJobId[preview.productionJobId]?.blockedReason);
				state.previewByJobId[preview.productionJobId] = preview;
				if (!state.jobs[preview.productionJobId] || wasBlocked) {
					state.jobs[preview.productionJobId] = buildDefaultJobSelection(preview);
				}
				if (preview.blockedReason) state.jobs[preview.productionJobId].enabled = false;
				for (const child of preview.affectedChildJobs) {
					if (child.blockedReason) {
						delete state.cascadeChildJobs[child.productionJobId];
					} else if (state.cascadeChildJobs[child.productionJobId] === undefined) {
						state.cascadeChildJobs[child.productionJobId] = true;
					}
				}
			}
		}
	}
});

export const {
	closeReapplyRecipes,
	closeReapplyRunView,
	finishReapplyRun,
	openReapplyRecipes,
	openReapplyRunView,
	resumeReapplyRuns,
	setActiveStep,
	setCascadeChildJob,
	setJobEnabled,
	trackStartedReapplyRun,
	untrackReapplyRun,
	updateAssignmentOverrideSelection,
	updateEventIdChangeSelection,
	updateRemovedVariationSelection,
	updateScalarFieldSelection,
	upsertPreviewResults
} = reapplyRecipesSlice.actions;

export default reapplyRecipesSlice.reducer;
